import { AnchorProvider } from "@coral-xyz/anchor";
import { calculateTokenAmount } from "@cremafinance/crema-sdk/dist/esm/src/math/liquity";
import { lamportPrice2uiPrice, tick2UiPrice } from "@cremafinance/crema-sdk/dist/esm/src/math/tick";
import { parsePositionsAccount, POSITIONS_ACCOUNT_SIZE } from "@cremafinance/crema-sdk/dist/esm/src/state/positions";
import type { TokenSwapAccount } from "@cremafinance/crema-sdk/dist/esm/src/state/tokenSwap";
import {
  isTokenSwapAccount,
  parseTokenSwapAccount,
  TOKEN_SWAP_ACCOUNT_SIZE,
} from "@cremafinance/crema-sdk/dist/esm/src/state/tokenSwap";
import { TokenSwap } from "@cremafinance/crema-sdk/dist/esm/src/tokenSwap";
import { captureException } from "@sentry/react";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { convert } from "@stepfinance/common";
import type { PendingReward } from "@stepfinance/liquidity";
import type { ConcentratedLiquidityPositions } from "@stepfinance/liquidity";
import { BN } from "bn.js";
import * as bs58 from "bs58";
import type Decimal from "decimal.js";
import { useCallback, useEffect, useMemo, useState } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";

import { useAccountsContext } from "../../contexts/accounts";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useMarkets } from "../../contexts/market";
import { useRefreshContext } from "../../contexts/refresh";
import { getTokenByMint } from "../../utils/tokenInfo";
import { useConnectedWallet } from "../wallet/useConnectedWallet";

const CREMA_API_URL = "https://api.crema.finance/config?name=swap-pairs";
const SWAP_V3_PROGRAM_ID: PublicKey = new PublicKey("6MLxLqiXaaSUpkgMnWDTuejNZEz3kE7k2woyHGVFw319");
const CREMA_PROGRAM_ID = "6MLxLqiXaaSUpkgMnWDTuejNZEz3kE7k2woyHGVFw319";

const handleCremaError = (err) => {
  console.error("Crema Error:", err);
  captureException(err);
};

export function useCremaPools() {
  const wallet = useConnectedWallet();
  const { connected } = useEnrichedWallet();
  const { connection } = useConnection();
  const { marketsInitialised, priceInUSD } = useMarkets();
  const { userTokenAccounts } = useAccountsContext();
  const { refreshID } = useRefreshContext();
  const [positions, setPositions] = useState<ConcentratedLiquidityPositions>([]);
  // type any to allow for null checking to only fetch api call once per page load
  const [poolStats, setPoolStats] = useState<any>(null);
  const [aprUpdated, setAprUpdated] = useState<boolean>(false);
  const [feesMap, setFeesMap] = useState<{ [TokenMint: string]: PendingReward[] }>({});
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    setPositions([]);
    setFeesMap({});
  }, [connected]);

  const provider = useMemo(() => {
    if (wallet) {
      return new AnchorProvider(connection, wallet, {
        commitment: "recent",
      });
    }
  }, [connection, wallet]);

  const fetchPoolStats = useCallback(async () => {
    try {
      const apiResp = await fetch("https://api.crema.finance/v1/swap/count");
      const apiData = await apiResp.json();
      const statsMap = {};
      apiData.data.pools.forEach((item) => {
        statsMap[item.swap_account] = item;
      });
      setPoolStats(statsMap);
    } catch (err) {
      handleCremaError(err);
    }
  }, []);

  useEffect(() => {
    void fetchPoolStats();
  }, [fetchPoolStats]);

  useDeepCompareEffect(() => {
    // decoupled apr update bc data coming from api
    if (positions.length > 0 && poolStats && !aprUpdated) {
      const results: ConcentratedLiquidityPositions = [];
      positions.forEach((item) => {
        const swapMint = item.lpMintAddress.toBase58();
        const statsObj = poolStats[swapMint];
        const newItem = { ...item };
        if (statsObj) {
          let apr = statsObj.apr.replace("%", "");
          apr = apr / 100;
          newItem.apr = apr;
        }
        results.push(newItem);
      });
      setPositions(results);
      setAprUpdated(true);
    }
  }, [positions, poolStats, aprUpdated]);

  const fetchRewards = useCallback(
    async ({
      tokenMint,
      swapMint,
      tokenADecimals,
      tokenBDecimals,
      tokenAPriceUSD,
      tokenBPriceUSD,
      tokenAInfo,
      tokenBInfo,
      lpName,
    }) => {
      if (provider) {
        try {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const swap: TokenSwap = new TokenSwap(provider, SWAP_V3_PROGRAM_ID, new PublicKey(swapMint));
          await swap.load();
          const feeObj: { amountA: Decimal; amountB: Decimal } = swap.preClaim(tokenMint);
          const rewardABalance = convert(feeObj.amountA.toNumber(), tokenADecimals);
          const rewardBBalance = convert(feeObj.amountB.toNumber(), tokenBDecimals);
          if (rewardABalance > 0 && rewardBBalance > 0) {
            const rewardAUSD = tokenAPriceUSD * rewardABalance;
            const rewardBUSD = tokenBPriceUSD * rewardBBalance;
            const result = [
              {
                rewardToken: tokenAInfo,
                rewardBalance: rewardABalance,
                rewardInUSD: rewardAUSD,
              },
              {
                rewardToken: tokenBInfo,
                rewardBalance: rewardBBalance,
                rewardInUSD: rewardBUSD,
              },
            ];
            setFeesMap((feesMap) => ({
              ...feesMap,
              [lpName]: result,
            }));
          }
        } catch (err) {
          handleCremaError(err);
        }
      }
    },
    [provider],
  );

  useDeepCompareEffect(() => {
    if (positions.length > 0) {
      const results: ConcentratedLiquidityPositions = [];
      positions.forEach((position) => {
        const feeList = feesMap[position.lpName];
        if (feeList) {
          position.rewards = [...feeList];
        }
        results.push({ ...position });
      });
      setPositions(results);
    }
  }, [feesMap]);

  useDeepCompareEffect(() => {
    const fetchSwapPositions = async (swapKey: string) => {
      const positionAccounts = await connection.getProgramAccounts(new PublicKey(CREMA_PROGRAM_ID), {
        filters: [
          { dataSize: POSITIONS_ACCOUNT_SIZE },
          {
            memcmp: {
              offset: 1,
              bytes: swapKey,
            },
          },
        ],
      });
      const swapPositions: any = [];
      for (const accountInfo of positionAccounts) {
        const list = parsePositionsAccount(accountInfo.pubkey, accountInfo.account);
        if (list?.data.positions === undefined) {
          continue;
        }
        for (const p of list.data.positions) {
          swapPositions.push({
            positionKey: accountInfo.pubkey,
            ...p,
          });
        }
      }
      return swapPositions;
    };
    const fetchOnChainPoolsData = async () => {
      const accounts = await connection.getProgramAccounts(SWAP_V3_PROGRAM_ID, {
        filters: [
          {
            memcmp: {
              offset: 33,
              bytes: bs58.encode(new BN(0).toArrayLike(Buffer, "le", 1)),
            },
          },
          {
            dataSize: TOKEN_SWAP_ACCOUNT_SIZE,
          },
        ],
      });
      const list: TokenSwapAccount[] = [];
      accounts.forEach((v) => {
        if (isTokenSwapAccount(v.account)) {
          const info = parseTokenSwapAccount(v.pubkey, v.account);
          if (info) {
            list.push(info.data);
          }
        }
      });
      return list;
    };
    const fetchPositions = async () => {
      try {
        setIsFetching(true);
        const poolsApiResponse = await (await fetch(CREMA_API_URL)).json();
        const poolsApiData = poolsApiResponse.data;
        const promiseArr: any[] = [];
        poolsApiData.forEach((pool) => {
          promiseArr.push(fetchSwapPositions(pool.swap_account));
        });
        const poolsObj = {};
        poolsApiData.forEach((item) => {
          poolsObj[item.swap_account] = item;
        });
        const poolsOnChainData = await fetchOnChainPoolsData();
        poolsOnChainData.forEach((item) => {
          const tokenSwapKey = item.tokenSwapKey.toBase58();
          const obj = poolsObj[tokenSwapKey];
          if (obj) {
            obj.onChain = item;
          }
        });
        const poolsData = poolsApiData.map((item) => poolsObj[item.swap_account]);
        const poolsPositionsOnChain = await Promise.all(promiseArr);
        const tokenAccountsMap = {};
        for (let i = 0; userTokenAccounts.length > 0 && i < userTokenAccounts.length; i++) {
          const tokenAccount = userTokenAccounts[i];
          const tokenAccountAddress = tokenAccount.pubkey;
          const mintAddress = tokenAccount.info.mint;
          const balance = tokenAccount.tokenAmount.uiAmount;
          if (Object.prototype.hasOwnProperty.call(tokenAccountsMap, mintAddress)) {
            if (tokenAccountsMap[mintAddress].balance < balance) {
              tokenAccountsMap[mintAddress] = {
                tokenAccountAddress,
                balance,
              };
            }
          } else {
            tokenAccountsMap[mintAddress] = {
              tokenAccountAddress,
              balance,
            };
          }
        }
        const activePositions: ConcentratedLiquidityPositions = [];
        poolsPositionsOnChain.forEach((positionsList, i) => {
          positionsList.forEach((item) => {
            const mintKey = item.nftTokenId.toBase58();
            if (tokenAccountsMap[mintKey] && tokenAccountsMap[mintKey].balance && provider) {
              const pool = poolsData[i];
              const minPrice = tick2UiPrice(item.lowerTick, pool.token_a.decimal, pool.token_b.decimal).toNumber();
              const maxPrice = tick2UiPrice(item.upperTick, pool.token_a.decimal, pool.token_b.decimal).toNumber();
              const currentPrice = lamportPrice2uiPrice(
                pool.onChain.currentSqrtPrice.pow(2),
                pool.token_a.decimal,
                pool.token_b.decimal,
              ).toNumber();
              const isBelowRange = currentPrice < minPrice;
              const isAboveRange = currentPrice > maxPrice;
              const { amountA, amountB } = calculateTokenAmount(
                item.lowerTick,
                item.upperTick,
                item.liquity,
                pool.onChain.currentSqrtPrice,
              );
              const tokenADecimals = pool.token_a.decimal;
              const tokenBDecimals = pool.token_b.decimal;
              const amountANum = convert(amountA.toNumber(), tokenADecimals);
              const amountBNum = convert(amountB.toNumber(), tokenBDecimals);
              const tokenAMint = pool.token_a.token_mint;
              const tokenBMint = pool.token_b.token_mint;
              const tokenAInfo = getTokenByMint(tokenAMint);
              const tokenBInfo = getTokenByMint(tokenBMint);
              if (!tokenAInfo || !tokenBInfo) {
                const unknownMints = [!tokenAInfo ? tokenAMint : "", !tokenBInfo ? tokenBMint : ""];
                const errorMsg = "Unknown Mint: " + unknownMints.join(" ");
                handleCremaError(errorMsg);
                return;
              }

              const tokenAPriceUSD = priceInUSD(tokenAMint) ?? 0;
              const tokenBPriceUSD = priceInUSD(tokenBMint) ?? 0;
              const valueInUSD = amountANum * tokenAPriceUSD + amountBNum * tokenBPriceUSD;
              const liquidity = [
                {
                  token: tokenAInfo,
                  amount: amountANum,
                  minPrice: 1 / minPrice,
                  maxPrice: 1 / maxPrice,
                },
                {
                  token: tokenBInfo,
                  amount: amountBNum,
                  minPrice: minPrice,
                  maxPrice: maxPrice,
                },
              ];
              const lpName = `${pool.token_a.symbol}/${pool.token_b.symbol}`;
              void fetchRewards({
                tokenMint: item.nftTokenId,
                swapMint: pool.swap_account,
                tokenADecimals,
                tokenBDecimals,
                tokenAPriceUSD,
                tokenBPriceUSD,
                tokenAInfo,
                tokenBInfo,
                lpName,
              });
              activePositions.push({
                key: `crema-pool-${mintKey}`,
                kind: "Crema",
                lpName,
                link: "https://app.crema.finance/#/pools",
                lpMintAddress: new PublicKey(pool.swap_account),
                liquidity,
                rewards: [],
                valueInUSD,
                // apr is first reported as 0
                // and then updated in effect above
                // for decoupled api fetching
                apr: 0,
                isAboveRange,
                isBelowRange,
              });
            }
          });
        });
        setPositions(activePositions);
      } catch (e) {
        console.error("Unexpected failure to fetch and/or calculate Crema Finance positions");
      } finally {
        setIsFetching(false);
      }
    };
    if (wallet && connection && marketsInitialised && userTokenAccounts.length > 0 && !isFetching) {
      void fetchPositions();
    }
  }, [wallet, connection, marketsInitialised, userTokenAccounts, refreshID]);

  return positions;
}
